import React, { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLeagueMembership } from "../context/LeagueMembershipContext";
import { useToast } from "../context/ToastContext";
import { useLeague } from "../hooks/useLeague";
import { useRequest } from "../hooks/useRequest";
import ModalDialog from "../Common/ModalDialog";
import PaginationControls from "../Common/PaginationControls";
import { getLeagueUserUrl, getLeagueUsersUrl } from "../config/api";
import { apiDelete, apiPatch, apiPost } from "../utils/apiClient";
import { getApiErrorMessages } from "../utils/apiErrors";
import {
  LEAGUE_ROLE_LABELS,
  assignableRoles,
  canManageRole,
} from "../utils/leagueRoles";
import { PaginatedResponse } from "../types/api";
import { CreatedLeagueUser, LeagueRole, LeagueUser } from "../types/models";

type SortField = "name" | "email" | "role" | "created_at";
type SortDirection = "asc" | "desc";

type View =
  | { mode: "list" }
  | { mode: "add" }
  | { mode: "edit"; leagueUser: LeagueUser }
  | { mode: "remove"; leagueUser: LeagueUser };

const ITEMS_PER_PAGE = 50;

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function displayName(leagueUser: LeagueUser) {
  return leagueUser.name || leagueUser.email;
}

function ErrorMessages({ messages }: { messages: string[] }) {
  if (messages.length === 0) return null;

  return (
    <Alert variant="danger">
      {messages.length === 1 ? (
        messages[0]
      ) : (
        <ul className="mb-0">
          {messages.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      )}
    </Alert>
  );
}

function FormActions({
  isSubmitting,
  onCancel,
}: {
  isSubmitting: boolean;
  onCancel: () => void;
}) {
  return (
    <>
      <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
        Cancel
      </Button>{" "}
      <Button variant="primary" type="submit" disabled={isSubmitting}>
        {isSubmitting ? (
          <>
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
            />{" "}
            Saving...
          </>
        ) : (
          "Save"
        )}
      </Button>
    </>
  );
}

function RoleSelect({
  roles,
  value,
  onChange,
  disabled,
}: {
  roles: LeagueRole[];
  value: LeagueRole;
  onChange: (role: LeagueRole) => void;
  disabled: boolean;
}) {
  return (
    <Form.Group className="mb-3" controlId="roleField">
      <Form.Label>Role</Form.Label>
      <Form.Select
        value={value}
        onChange={(e) => onChange(e.target.value as LeagueRole)}
        disabled={disabled}
      >
        {roles.map((role) => (
          <option key={role} value={role}>
            {LEAGUE_ROLE_LABELS[role]}
          </option>
        ))}
      </Form.Select>
      <Form.Text className="text-muted">
        Owners manage everything, including owner users and league deletion.
        Admins manage league settings, seasons, teams, players, schedules, and
        non-owner users. Scorekeepers can view league data and score games.
      </Form.Text>
    </Form.Group>
  );
}

function SortableHeader({
  field,
  label,
  sortField,
  sortDirection,
  onSort,
}: {
  field: SortField;
  label: string;
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
}) {
  const indicator =
    sortField !== field ? (
      <span className="text-muted"> ⇅</span>
    ) : (
      <span>{sortDirection === "asc" ? " ▲" : " ▼"}</span>
    );

  return (
    <th style={{ cursor: "pointer" }} onClick={() => onSort(field)}>
      {label}
      {indicator}
    </th>
  );
}

function UsersList({
  currentMembership,
  sortField,
  sortDirection,
  page,
  onSort,
  onPageChange,
  onAddClick,
  onEditClick,
  onRemoveClick,
}: {
  currentMembership?: LeagueUser;
  sortField: SortField;
  sortDirection: SortDirection;
  page: number;
  onSort: (field: SortField) => void;
  onPageChange: (page: number) => void;
  onAddClick: () => void;
  onEditClick: (leagueUser: LeagueUser) => void;
  onRemoveClick: (leagueUser: LeagueUser) => void;
}) {
  const auth = useAuth();
  const { currentLeague } = useLeague();

  const sortParam = sortDirection === "desc" ? `-${sortField}` : sortField;
  const usersUrl = currentLeague
    ? `${getLeagueUsersUrl(currentLeague.id)}?limit=${ITEMS_PER_PAGE}&skip=${
        (page - 1) * ITEMS_PER_PAGE
      }&sort=${sortParam}`
    : undefined;

  const { data: response, error } = useRequest<PaginatedResponse<LeagueUser>>(
    usersUrl,
    auth.token,
  );

  const actorRole = currentMembership?.role;
  const headerProps = { sortField, sortDirection, onSort };

  return (
    <div className="admin-content">
      <Row className="mb-2">
        <Col>
          <h2>Users</h2>
        </Col>
        {assignableRoles(actorRole).length > 0 && (
          <Col className="d-flex justify-content-end align-items-start">
            <Button onClick={onAddClick} variant="primary">
              Add User
            </Button>
          </Col>
        )}
      </Row>
      <Row>
        <Col>
          {error && (
            <Alert variant="danger">Error loading users: {error.message}</Alert>
          )}
          {!response && !error && (
            <div className="d-flex justify-content-center">
              <Spinner animation="grow" /> Loading...
            </div>
          )}
          {response && (
            <>
              <Table striped bordered hover responsive className="crud">
                <thead>
                  <tr>
                    <SortableHeader
                      field="name"
                      label="Name"
                      {...headerProps}
                    />
                    <SortableHeader
                      field="email"
                      label="Email"
                      {...headerProps}
                    />
                    <SortableHeader
                      field="role"
                      label="Role"
                      {...headerProps}
                    />
                    <SortableHeader
                      field="created_at"
                      label="Added"
                      {...headerProps}
                    />
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {response.data.map((leagueUser) => (
                    <tr key={leagueUser.id}>
                      <td>
                        {leagueUser.name}
                        {leagueUser.user_id === auth.user?.id && (
                          <Badge bg="secondary" className="ms-2">
                            You
                          </Badge>
                        )}
                      </td>
                      <td>{leagueUser.email}</td>
                      <td>{LEAGUE_ROLE_LABELS[leagueUser.role]}</td>
                      <td>{formatDate(leagueUser.created_at)}</td>
                      <td align="right">
                        {canManageRole(actorRole, leagueUser.role) && (
                          <>
                            <Button onClick={() => onEditClick(leagueUser)}>
                              Edit
                            </Button>
                            &nbsp;
                            <Button
                              variant="danger"
                              onClick={() => onRemoveClick(leagueUser)}
                            >
                              Remove
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {response.total > ITEMS_PER_PAGE && (
                <PaginationControls
                  currentPage={page}
                  totalItems={response.total}
                  itemsPerPage={ITEMS_PER_PAGE}
                  onPageChange={onPageChange}
                />
              )}
            </>
          )}
        </Col>
      </Row>
    </div>
  );
}

function AddUserForm({
  actorRole,
  onCancel,
  onSaved,
}: {
  actorRole: LeagueRole;
  onCancel: () => void;
  onSaved: (leagueUser: CreatedLeagueUser) => void;
}) {
  const auth = useAuth();
  const { currentLeague } = useLeague();
  const roles = assignableRoles(actorRole);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<LeagueRole>(
    roles.includes("scorekeeper") ? "scorekeeper" : roles[0],
  );
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!currentLeague) return;

    setIsSubmitting(true);
    setErrorMessages([]);

    try {
      const created = await apiPost<CreatedLeagueUser>(
        getLeagueUsersUrl(currentLeague.id),
        {
          league_user: {
            email: email.trim(),
            role,
            name: name.trim() || undefined,
            password: password || undefined,
          },
        },
        { token: auth.token, leagueAbbr: currentLeague.abbr },
      );
      onSaved(created);
    } catch (error) {
      setErrorMessages(
        getApiErrorMessages(error, "An error occurred while adding the user"),
      );
      setIsSubmitting(false);
    }
  };

  return (
    <div className="admin-content">
      <Container>
        <Row>
          <Col md={8}>
            <h1>Add User</h1>
            <ErrorMessages messages={errorMessages} />

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3" controlId="emailField">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                  autoComplete="off"
                  required
                />
              </Form.Group>

              <RoleSelect
                roles={roles}
                value={role}
                onChange={setRole}
                disabled={isSubmitting}
              />

              <fieldset className="mb-3">
                <legend className="fs-6 fw-semibold mb-1">New account</legend>
                <p className="text-muted small">
                  If an account with this email already exists it is added to
                  the league unchanged, and the fields below are ignored.
                  Otherwise a new account is created with this name and
                  temporary password. Share the password with the user so they
                  can sign in and change it.
                </p>
                <Form.Group className="mb-3" controlId="nameField">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={isSubmitting}
                    autoComplete="off"
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="passwordField">
                  <Form.Label>Temporary Password</Form.Label>
                  <Form.Control
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isSubmitting}
                    autoComplete="new-password"
                  />
                </Form.Group>
              </fieldset>

              <FormActions isSubmitting={isSubmitting} onCancel={onCancel} />
            </Form>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

function EditUserForm({
  leagueUser,
  actorRole,
  onCancel,
  onSaved,
}: {
  leagueUser: LeagueUser;
  actorRole: LeagueRole;
  onCancel: () => void;
  onSaved: (leagueUser: LeagueUser) => void;
}) {
  const auth = useAuth();
  const { currentLeague } = useLeague();
  const [role, setRole] = useState<LeagueRole>(leagueUser.role);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!currentLeague) return;

    setIsSubmitting(true);
    setErrorMessages([]);

    try {
      const updated = await apiPatch<LeagueUser>(
        getLeagueUserUrl(leagueUser.id),
        { league_user: { role } },
        { token: auth.token, leagueAbbr: currentLeague.abbr },
      );
      onSaved(updated);
    } catch (error) {
      setErrorMessages(
        getApiErrorMessages(error, "An error occurred while saving the user"),
      );
      setIsSubmitting(false);
    }
  };

  return (
    <div className="admin-content">
      <Container>
        <Row>
          <Col md={8}>
            <h1>Edit User</h1>
            <ErrorMessages messages={errorMessages} />

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3" controlId="nameField">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  plaintext
                  readOnly
                  value={leagueUser.name ?? ""}
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="emailField">
                <Form.Label>Email</Form.Label>
                <Form.Control plaintext readOnly value={leagueUser.email} />
              </Form.Group>

              <RoleSelect
                roles={assignableRoles(actorRole)}
                value={role}
                onChange={setRole}
                disabled={isSubmitting}
              />

              <FormActions isSubmitting={isSubmitting} onCancel={onCancel} />
            </Form>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

function RemoveUserConfirmation({
  leagueUser,
  onCancel,
  onRemoved,
}: {
  leagueUser: LeagueUser;
  onCancel: () => void;
  onRemoved: () => void;
}) {
  const auth = useAuth();
  const toast = useToast();
  const { currentLeague } = useLeague();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRemove = async () => {
    if (!currentLeague || isSubmitting) return;
    setIsSubmitting(true);

    try {
      await apiDelete(getLeagueUserUrl(leagueUser.id), {
        token: auth.token,
        leagueAbbr: currentLeague.abbr,
      });
      onRemoved();
    } catch (error) {
      toast.showToast(
        getApiErrorMessages(error, "Failed to remove the user").join(" "),
        "danger",
      );
      onCancel();
    }
  };

  return (
    <div
      className="modal show"
      style={{ display: "block", position: "initial" }}
    >
      <ModalDialog
        titleText="Remove User"
        bodyText={`Remove ${displayName(leagueUser)} (${
          leagueUser.email
        }) from ${
          currentLeague?.name ?? "this league"
        }? Their account is not deleted, and they can be added again later.`}
        okText="Remove"
        onCancelClick={onCancel}
        onSaveClick={handleRemove}
      />
    </div>
  );
}

function AdminUsersPage() {
  const auth = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const { currentLeague, refreshLeagues } = useLeague();
  const [view, setView] = useState<View>({ mode: "list" });
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [page, setPage] = useState(1);

  // The signed-in user's own membership decides which actions are offered.
  // The API enforces the same rules regardless.
  const {
    membership: currentMembership,
    error: membershipError,
    refreshMembership,
  } = useLeagueMembership();

  useEffect(() => {
    setView({ mode: "list" });
    setPage(1);
  }, [currentLeague?.id]);

  const showList = () => setView({ mode: "list" });

  const isCurrentUser = (leagueUser: LeagueUser) =>
    leagueUser.user_id === auth.user?.id;

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setPage(1);
  };

  const handleAdded = (created: CreatedLeagueUser) => {
    const role = LEAGUE_ROLE_LABELS[created.role];
    toast.showToast(
      created.account_created
        ? `Created an account for ${created.email} and added them as ${role}`
        : `Added ${displayName(created)} as ${role}`,
      "success",
    );
    showList();
  };

  const handleUpdated = (updated: LeagueUser) => {
    toast.showToast(
      `${displayName(updated)} is now ${LEAGUE_ROLE_LABELS[updated.role]}`,
      "success",
    );
    if (isCurrentUser(updated)) {
      // Changing your own role changes what you're allowed to do here.
      refreshMembership();
      refreshLeagues();
    }
    showList();
  };

  const handleRemoved = (removed: LeagueUser) => {
    toast.showToast(`Removed ${displayName(removed)}`, "success");
    if (isCurrentUser(removed)) {
      // You no longer belong to this league, so leave its admin area.
      refreshLeagues();
      navigate("/leagues");
      return;
    }
    showList();
  };

  if (!currentLeague) {
    return (
      <div className="admin-content">
        <Alert variant="warning">No league selected</Alert>
      </div>
    );
  }

  if (membershipError) {
    return (
      <div className="admin-content">
        <Alert variant="danger">
          Error loading your league membership: {membershipError.message}
        </Alert>
      </div>
    );
  }

  const actorRole = currentMembership?.role;

  switch (view.mode) {
    case "add":
      return actorRole ? (
        <AddUserForm
          actorRole={actorRole}
          onCancel={showList}
          onSaved={handleAdded}
        />
      ) : null;
    case "edit":
      return actorRole ? (
        <EditUserForm
          leagueUser={view.leagueUser}
          actorRole={actorRole}
          onCancel={showList}
          onSaved={handleUpdated}
        />
      ) : null;
    case "remove":
      return (
        <RemoveUserConfirmation
          leagueUser={view.leagueUser}
          onCancel={showList}
          onRemoved={() => handleRemoved(view.leagueUser)}
        />
      );
    default:
      return (
        <UsersList
          currentMembership={currentMembership}
          sortField={sortField}
          sortDirection={sortDirection}
          page={page}
          onSort={handleSort}
          onPageChange={setPage}
          onAddClick={() => setView({ mode: "add" })}
          onEditClick={(leagueUser) => setView({ mode: "edit", leagueUser })}
          onRemoveClick={(leagueUser) =>
            setView({ mode: "remove", leagueUser })
          }
        />
      );
  }
}

export default AdminUsersPage;
